import { OriginsStepEnum } from '../components/insights/origins/origins.component';

import { FeatureIdEnum, KeywordsModeEnum, LocaleEnum, OriginIdEnum } from '../enums';

import { Dictionary, InsightsMessages } from '../models';

const DEFAULT_ORIGINS_MESSAGES_EN = {
  [OriginIdEnum.DEFAULT]: {
    description: 'Any query typed in the search box.'
  },
  [OriginIdEnum.EMPATHIZE_TRENDING]: {
    description: 'Query selected from the popular searches.'
  },
  [OriginIdEnum.HISTORY]: {
    description: 'Query selected from the search history.'
  },
  [OriginIdEnum.EMPATHIZE]: {
    description: 'Query selected from the suggestions.'
  },
  [OriginIdEnum.EMPATHIZE_CATEGORY]: {
    description: 'Query filtered by category selected from the suggestions.'
  },
  [OriginIdEnum.RELATED_TAG]: {
    description: 'Query generated from a related tag.'
  },
  [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: {
    description: 'Query selected from the next queries suggestions list.'
  },
  [OriginIdEnum.NEXT_QUERY]: {
    description: 'Query generated from a next query of the results grid.'
  },
  [OriginIdEnum.PARTIALS]: {
    description: 'Query generated from a partial suggestion.'
  },
  [OriginIdEnum.SPELLCHECK]: {
    description: 'Query generated from a spell check suggestion.'
  }
};

const INSIGHTS_MESSAGES_EN: InsightsMessages = {
  landing: {
    card: {
      descriptions: {
        [FeatureIdEnum.NOW]: 'Explore what’s happening now in your commerce search. Visualize the number of queries, clicks, and no results for the past few hours.',
        [FeatureIdEnum.ORIGINS]: 'Examine how shoppers use your commerce search. Visualize the number of queries by organic searches, predictive, or refinement features.',
        [FeatureIdEnum.KEYWORDS]: 'Discover new search term trends even before they are truly trending. Monitor the top 10 search terms used in search queries.'
      },
      discover: 'Discover'
    }
  },
  origins: {
    landing: {
      paragraph1: 'The Search gives customers different ways to find what they are looking for: type a query or click a suggestion.',
      paragraph2: `The origin of this query could be <b>Default</b>, <b>Predictive</b> or <b>Refinement</b>.`
    },
    steps: {
      [OriginsStepEnum.DEFAULT]: {
        label: 'Default',
        description: 'These queries are written without any kind of search suggestions. The customer types exactly what they are looking for.'
      },
      [OriginsStepEnum.PREDICTIVE]: {
        label: 'Predictive',
        description: 'The customer doesn\'t type any query; they simply choose from relevant suggestions provided.'
      },
      [OriginsStepEnum.REFINEMENT]: {
        label: 'Refinement',
        description: 'A combination of a query and some suggestions offered within the Search experience.'
      }
    },
    component: {
      origins: {
        [OriginIdEnum.DEFAULT]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.DEFAULT],
        [OriginIdEnum.DEFAULT_SEARCH]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.DEFAULT],
        [OriginIdEnum.EMPATHIZE_TRENDING]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE_TRENDING],
        [OriginIdEnum.TRENDING]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE_TRENDING],
        [OriginIdEnum.HISTORY]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.HISTORY],
        [OriginIdEnum.HISTORICAL]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.HISTORY],
        [OriginIdEnum.EMPATHIZE]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE],
        [OriginIdEnum.AUTOCOMPLETE]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE],
        [OriginIdEnum.EMPATHIZE_CATEGORY]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE_CATEGORY],
        [OriginIdEnum.RELATED_TAG]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED_TAGS]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE_NEXT_QUERIES],
        [OriginIdEnum.NEXT_QUERY]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.EMPATHIZE_NEXT_QUERIES],
        [OriginIdEnum.WHATS_NEXT_CAROUSEL]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.PARTIALS]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.PARTIALS],
        [OriginIdEnum.SPELLCHECK]: DEFAULT_ORIGINS_MESSAGES_EN[OriginIdEnum.SPELLCHECK]
      }
    }
  },
  keywords: {
    main: {
      titles: {
        top: 'Top {count} terms by {mode}',
        compare: 'Comparative'
      },
      info: '<b>Growth:</b> Search terms trending now.<br> <b>Popularity:</b> Overall top search terms.',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Growth',
        [KeywordsModeEnum.POPULARITY]: 'Popularity'
      },
      noDataFound: 'No data found for this period and filters. Please, try other values.'
    },
    aside: {
      title: 'Top queries',
      noQueries: 'No queries found for this term'
    }
  }
};

const DEFAULT_ORIGINS_MESSAGES_ES = {
  [OriginIdEnum.DEFAULT]: {
    description: 'Cualquier query escrita por el usuario en la caja de búsqueda.'
  },
  [OriginIdEnum.EMPATHIZE_TRENDING]: {
    description: 'Query seleccionada desde las búsquedas populares.'
  },
  [OriginIdEnum.HISTORY]: {
    description: 'Query seleccionada desde el historial.'
  },
  [OriginIdEnum.EMPATHIZE]: {
    description: 'Query seleccionada desde las sugerencias.'
  },
  [OriginIdEnum.EMPATHIZE_CATEGORY]: {
    description: 'Query filtrada por categoría seleccionada desde las sugerencias.'
  },
  [OriginIdEnum.RELATED_TAG]: {
    description: 'Query generada desde una related tag.'
  },
  [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: {
    description: 'Query seleccionada desde las sugerencias de next queries.'
  },
  [OriginIdEnum.NEXT_QUERY]: {
    description: 'Query generada desde una next query del grid de resultados.'
  },
  [OriginIdEnum.PARTIALS]: {
    description: 'Query generada desde una sugerencia parcial.'
  },
  [OriginIdEnum.SPELLCHECK]: {
    description: 'Query generada por una sugerencia del corrector.'
  }
};

const INSIGHTS_MESSAGES_ES: InsightsMessages = {
  landing: {
    card: {
      descriptions: {
        [FeatureIdEnum.NOW]: 'Descubre lo que está pasando ahora con la experiencia de búsqueda. ' +
        'Visualiza las consultas con y sin resultados y los clics realizados en las últimas horas.',
        [FeatureIdEnum.ORIGINS]: 'Analiza cómo los consumidores utilizan la experiencia de búsqueda. ' +
        'Visualiza el número de consultas orgánicas, predictivas o de refinamiento.',
        [FeatureIdEnum.KEYWORDS]: 'Descubre nuevos términos de búsqueda incluso antes de que se pongan de moda ' +
        'y comprueba los 10 términos más usados en las consultas.'
      },
      discover: 'Descubrir'
    }
  },
  origins: {
    landing: {
      paragraph1: 'La experiencia de búsqueda proporciona diferentes vías para encontrar lo que estás buscando: ya sea escribiendo ' +
        'una query o escogiendo entre distintas sugerencias.',
      paragraph2: `El origen de la query puede ser de tipo <b>Default</b>, <b>Predictive</b> o <b>Refinement</b>`
    },
    steps: {
      [OriginsStepEnum.DEFAULT]: {
        label: 'Default',
        description: 'Queries escritas sin ningún tipo de sugerencia externa: se escribe exactamemente lo que se está buscando.'
      },
      [OriginsStepEnum.PREDICTIVE]: {
        label: 'Predictive',
        description: 'La búsqueda se realiza a través de las sugerencias ofrecidas, sin escribir ninguna query.'
      },
      [OriginsStepEnum.REFINEMENT]: {
        label: 'Refinement',
        description: 'La búsqueda es la combinación de una query con las sugerencias ofrecidas'
      }
    },
    component: {
      origins: {
        [OriginIdEnum.DEFAULT]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.DEFAULT],
        [OriginIdEnum.DEFAULT_SEARCH]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.DEFAULT],
        [OriginIdEnum.EMPATHIZE_TRENDING]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE_TRENDING],
        [OriginIdEnum.TRENDING]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE_TRENDING],
        [OriginIdEnum.HISTORY]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.HISTORY],
        [OriginIdEnum.HISTORICAL]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.HISTORY],
        [OriginIdEnum.EMPATHIZE]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE],
        [OriginIdEnum.AUTOCOMPLETE]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE],
        [OriginIdEnum.EMPATHIZE_CATEGORY]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE_CATEGORY],
        [OriginIdEnum.RELATED_TAG]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED_TAGS]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.RELATED]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.RELATED_TAG],
        [OriginIdEnum.EMPATHIZE_NEXT_QUERIES]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE_NEXT_QUERIES],
        [OriginIdEnum.NEXT_QUERY]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.WHATS_NEXT]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.EMPATHIZE_NEXT_QUERIES],
        [OriginIdEnum.WHATS_NEXT_CAROUSEL]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.NEXT_QUERY],
        [OriginIdEnum.PARTIALS]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.PARTIALS],
        [OriginIdEnum.SPELLCHECK]: DEFAULT_ORIGINS_MESSAGES_ES[OriginIdEnum.SPELLCHECK]
      }
    }
  },
  keywords: {
    main: {
      titles: {
        top: 'Top {count} terms por {mode}',
        compare: 'Comparativa'
      },
      info: '<b>Crecimiento:</b> Términos de búsqueda en auge.<br> <b>Popularidad:</b> Términos de búsqueda principales.',
      modes: {
        [KeywordsModeEnum.GROWTH]: 'Crecimiento',
        [KeywordsModeEnum.POPULARITY]: 'Popularidad'
      },
      noDataFound: 'No se han encontrado datos para este periodo y filtros. Por favor, pruebe otros valores.'
    },
    aside: {
      title: 'Top queries',
      noQueries: 'No se han encontrado queries para este term'
    }
  }
};

export const INSIGHTS_MESSAGES: Dictionary<{ insights: InsightsMessages }> = {
  [LocaleEnum.ENGLISH]: {
    insights: INSIGHTS_MESSAGES_EN
  },
  [LocaleEnum.SPANISH]: {
    insights: INSIGHTS_MESSAGES_ES
  }
};
